/* Bloom Motion — custom code (not part of Dawn).
 * <bloom-bundle-selector> + <bloom-sticky-atc> + <bloom-gallery>
 * Depends on Dawn globals loaded with defer in <head>: constants.js (PUB_SUB_EVENTS),
 * pubsub.js (publish/subscribe), global.js (fetchConfig), window.routes.
 *
 * Model: `units` = one variant per item (length = highest tier quantity). A tier with qty N uses
 * units[0..N-1]. The main colour swatches set every unit; the per-item rows set one unit each.
 * Tier total = qty × variant price − the tier's data-off (mirrors the store's automatic Buy-N discounts).
 */
(() => {
  if (customElements.get('bloom-bundle-selector')) return;

  /* ---------- helpers ---------- */

  // Dawn has no Shopify.formatMoney → minimal formatter for the common formats.
  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = Number(cents || 0) / 100;
    const m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = m ? m[1] : 'amount';
    let out;
    switch (key) {
      case 'amount_no_decimals':
        out = Math.round(value).toLocaleString('en-US');
        break;
      case 'amount_with_comma_separator':
        out = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = Math.round(value).toLocaleString('de-DE');
        break;
      default:
        out = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  const getCartElement = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  class BloomCartError extends Error {
    constructor(payload, status) {
      super(
        payload?.description ||
          (typeof payload?.errors === 'string' ? payload.errors : null) ||
          payload?.message ||
          'Could not add to cart.'
      );
      this.payload = payload;
      this.status = status;
    }
  }

  // Multi-line add via /cart/add.js, asking Shopify to render the cart drawer sections (≤5).
  async function addItemsToCart(items, { render = true } = {}) {
    const cart = getCartElement();
    const sectionIds =
      cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((s) => s.section || s.id)
        : [];

    const body = { items };
    if (sectionIds.length) {
      body.sections = [...new Set(sectionIds)].slice(0, 5).join(',');
      body.sections_url = window.location.pathname;
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(body);

    const res = await fetch(window.routes.cart_add_url, config);
    let json = null;
    try {
      json = await res.json();
    } catch (_) {
      /* empty body */
    }

    if (!res.ok || json?.status) {
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'bloom-bundle',
          productVariantId: items[0]?.id,
          errors: json?.description || json?.errors || json?.message,
          message: json?.message,
        });
      }
      throw new BloomCartError(json || {}, res.status);
    }

    if (render && cart && json.sections) {
      if (cart.tagName === 'CART-NOTIFICATION') {
        const first = json.items?.[0];
        cart.renderContents({ ...json, id: first?.variant_id, key: first?.key });
      } else {
        // A drawer rendered while the cart was empty keeps .is-empty (no padding, hidden header,
        // grid layout) unless we clear it before rendering — same as Dawn's product-form.
        cart.classList.remove('is-empty');
        cart.renderContents(json); // CartDrawer.renderContents replaces HTML and open()s the drawer
      }
    } else if (render && !cart) {
      window.location = window.routes.cart_url; // cart_type = page
    }

    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bloom-bundle', productVariantId: items[0]?.id, cartData: json });
    }
    return json;
  }

  // Re-render drawer + bubble from the server without opening (used after an error / partial add).
  async function refreshCartSections() {
    const ids = ['cart-drawer', 'cart-icon-bubble'];
    try {
      const res = await fetch(`${window.routes.cart_url}?sections=${ids.join(',')}`, {
        headers: { Accept: 'application/json' },
      });
      const sections = await res.json();
      const parse = (html, sel) => new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
      if (sections['cart-drawer']) {
        const fresh = parse(sections['cart-drawer'], '#CartDrawer');
        const current = document.querySelector('#CartDrawer');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        const drawer = document.querySelector('cart-drawer');
        const freshDrawer = parse(sections['cart-drawer'], 'cart-drawer');
        if (drawer && freshDrawer) drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }
      if (sections['cart-icon-bubble']) {
        const fresh = parse(sections['cart-icon-bubble'], '.shopify-section');
        const current = document.getElementById('cart-icon-bubble');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
    } catch (_) {
      /* non-blocking */
    }
  }

  /* ---------- <bloom-bundle-selector> ---------- */

  class BloomBundleSelector extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      try {
        this.variants = JSON.parse(this.querySelector('script[data-variants]')?.textContent || '[]');
      } catch (_) {
        this.variants = [];
      }
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.form.querySelector('[data-submit-label]');
      this.errorEl = this.form.querySelector('[data-error]');
      this.statusEl = this.form.querySelector('[data-status]');
      this.unitsEl = this.form.querySelector('[data-units]');
      this.unitRows = Array.from(this.form.querySelectorAll('[data-unit-row]'));
      this.expressEl = this.form.querySelector('[data-express]');
      this.mixedNote = this.form.querySelector('[data-mixed-note]');
      this.colorLabel = this.form.querySelector('[data-color-label]');
      this.moneyFormat = this.dataset.moneyFormat;

      const start = this.byId(this.dataset.variantId) || this.variants[0];
      const maxQty = Math.max(1, this.unitRows.length, ...this.tierInputs().map((i) => Number(i.dataset.qty || 1)));
      this.units = Array.from({ length: maxQty }, () => start);

      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('change', this.onChange);

      // Keep in sync if a Dawn product section on the same page changes variant.
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const v = event?.data?.variant;
          if (!v || String(v.product_id || '') !== String(this.dataset.productId)) return;
          const mine = this.byId(v.id);
          if (mine) this.setAll(mine);
        });
      }

      this.render();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('change', this.onChange);
      this.unsubscribe?.();
    }

    byId(id) {
      return this.variants.find((v) => String(v.id) === String(id));
    }

    byColor(color) {
      const matches = this.variants.filter((v) => v.color === color);
      return matches.find((v) => v.available) || matches[0];
    }

    tierInputs() {
      return Array.from(this.form.querySelectorAll('.bloom-tier__input'));
    }

    get selected() {
      return this.form.querySelector('.bloom-tier__input:checked') || this.tierInputs()[0];
    }

    get qty() {
      return Math.max(1, Number(this.selected?.dataset.qty || 1));
    }

    activeUnits(qty = this.qty) {
      return this.units.slice(0, qty);
    }

    isMixed(qty = this.qty) {
      const ids = new Set(this.activeUnits(qty).map((v) => v?.id));
      return ids.size > 1;
    }

    setAll(variant) {
      this.units = this.units.map(() => variant);
      this.render();
      this.showMedia(variant);
    }

    onChange(event) {
      const t = event.target;
      if (t.name && t.name.startsWith('bloom-color-')) {
        const v = this.byColor(t.value);
        if (v) this.setAll(v);
        return;
      }
      if (t.dataset.unitIndex !== undefined) {
        const v = this.byColor(t.value);
        const i = Number(t.dataset.unitIndex);
        if (v && i < this.units.length) {
          this.units[i] = v;
          this.showMedia(v);
        }
        this.render();
        return;
      }
      if (t.classList.contains('bloom-tier__input')) {
        this.render();
        return;
      }
    }

    showMedia(variant) {
      if (!variant?.media_id) return;
      const gallery = document.getElementById(this.dataset.gallery);
      gallery?.showMedia?.(variant.media_id);
    }

    totalsFor(qty) {
      const units = this.activeUnits(qty);
      const tier = this.tierInputs().find((i) => Number(i.dataset.qty) === qty);
      const off = Number(tier?.dataset.off || 0);
      const total = Math.max(0, units.reduce((s, v) => s + Number(v?.price || 0), 0) - off);
      const was = units.reduce((s, v) => s + Math.max(Number(v?.compare || 0), Number(v?.price || 0)), 0);
      return { total, was, perUnit: Math.round(total / qty), available: units.every((v) => v?.available) };
    }

    render() {
      const fmt = (c) => formatMoney(c, this.moneyFormat);
      const qty = this.qty;
      const units = this.activeUnits(qty);
      const first = units[0];
      const mixed = this.isMixed(qty);
      const shipAt = Number(this.dataset.shipThreshold || 0);

      // Tier rows: prices + 1 + 1 (+ 1) thumbnails reflect each unit's colour.
      this.tierInputs().forEach((input) => {
        const row = input.closest('.bloom-tier');
        const q = Math.max(1, Number(input.dataset.qty || 1));
        const { total, was, perUnit } = this.totalsFor(q);
        const saving = was - total;
        row.querySelector('[data-total]').textContent = fmt(total);
        const wasEl = row.querySelector('[data-was]');
        const saveEl = row.querySelector('[data-save]');
        wasEl.hidden = saving <= 0;
        wasEl.textContent = fmt(was);
        saveEl.hidden = saving <= 0;
        saveEl.textContent = `Save ${fmt(saving).replace(/\.00(?=[^0-9]*$)/, '')}`;
        const unitEl = row.querySelector('[data-unit]');
        if (unitEl) unitEl.textContent = q > 1 ? `${fmt(perUnit)} each` : '';
        const shipEl = row.querySelector('[data-ship]');
        if (shipEl) shipEl.hidden = !(shipAt > 0 && total >= shipAt);
        const paidEl = row.querySelector('[data-ship-paid]');
        if (paidEl) paidEl.hidden = !(shipAt > 0 && total < shipAt);
        row.querySelectorAll('[data-thumb-index]').forEach((thumb) => {
          const v = this.units[Number(thumb.dataset.thumbIndex)] || first;
          if (!v?.image) return;
          let img = thumb.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.width = 48;
            img.height = 48;
            img.alt = '';
            thumb.appendChild(img);
          }
          if (img.getAttribute('src') !== v.image) {
            img.removeAttribute('srcset');
            img.src = v.image;
          }
        });
      });

      // Main price block = one unit of the first item's variant.
      const block = document.querySelector(`[data-price-block="${this.dataset.sectionId}"]`);
      if (block && first) {
        const pct = first.compare > first.price ? Math.floor(((first.compare - first.price) * 100) / first.compare) : 0;
        block.querySelector('[data-main-price]').textContent = fmt(first.price);
        const w = block.querySelector('[data-main-was]');
        const p = block.querySelector('[data-main-pct]');
        w.hidden = pct <= 0;
        w.textContent = fmt(first.compare);
        p.hidden = pct <= 0;
        p.textContent = `Save ${pct}%`;
      }

      // Main swatches: checked when every active unit shares one colour, label "Mixed" otherwise.
      this.form.querySelectorAll('input[name^="bloom-color-"]').forEach((r) => {
        r.checked = !mixed && first && r.value === first.color;
      });
      if (this.colorLabel) this.colorLabel.textContent = mixed ? 'Mixed' : first?.color || '';

      // Colour pickers live INSIDE the selected tier card: move the per-item panel right after it.
      const mainSw = this.form.querySelector('[data-main-swatches]');
      if (mainSw) mainSw.hidden = true;
      const selCard = this.selected?.closest('.bloom-tier');
      if (this.unitsEl && selCard && selCard.nextElementSibling !== this.unitsEl) selCard.after(this.unitsEl);
      if (this.unitsEl) {
        this.unitsEl.hidden = false;
        const head = this.unitsEl.querySelector('[data-units-heading]');
        if (head) head.textContent = qty > 1 ? (head.dataset.many || 'Pick a colour for each') : (head.dataset.one || 'Pick your colour');
      }
      this.unitRows.forEach((row, i) => {
        row.hidden = i >= qty;
        const lg = row.querySelector('.bloom-unit__label');
        if (lg) lg.textContent = qty > 1 ? `Trainer ${i + 1}` : 'Colour';
        const v = this.units[i];
        const im = row.querySelector('[data-unit-thumb]');
        if (im && v?.image) im.src = v.image;
        row.querySelectorAll('input[type="radio"]').forEach((r) => {
          r.checked = Boolean(v) && r.value === v.color;
        });
      });

      // Submit button.
      const { total, available } = this.totalsFor(qty);
      const addText = window.variantStrings?.addToCart || 'Add to cart';
      const soldText = window.variantStrings?.soldOut || 'Sold out';
      this.available = available;
      this.submitBtn.disabled = !available;
      this.submitLabel.textContent = available ? `${addText} · ${fmt(total)}` : soldText;

      // Express checkout buys ONE variant line: sync it when all units match, hide it when mixed.
      const idField = this.form.querySelector('input[name="id"]');
      const qtyField = this.form.querySelector('input[name="quantity"]');
      if (idField && first) idField.value = first.id;
      if (qtyField) qtyField.value = qty;
      if (this.expressEl) this.expressEl.hidden = mixed;
      if (this.mixedNote) this.mixedNote.hidden = !mixed;

      const label = this.selected?.dataset.label || '';
      const colours = mixed ? units.map((v) => v?.color).join(' + ') : first?.color || '';
      this.dispatchEvent(
        new CustomEvent('bloom:offer-change', {
          bubbles: true,
          detail: { label: colours ? `${label} · ${colours}` : label, price: fmt(total), available },
        })
      );
    }

    // One line per variant, quantities summed (e.g. Pink × 1 + White × 1).
    buildItems() {
      const counts = new Map();
      this.activeUnits().forEach((v) => {
        if (!v) return;
        counts.set(v.id, (counts.get(v.id) || 0) + 1);
      });
      return Array.from(counts, ([id, quantity]) => ({ id, quantity }));
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitBtn.disabled || this.submitBtn.getAttribute('aria-disabled') === 'true') return;
      const items = this.buildItems();
      if (!items.length) return;

      this.setLoading(true);
      this.showError('');
      this.showStatus('');
      const render = this.dataset.openDrawer !== 'false' && !window.Shopify?.designMode;
      try {
        await addItemsToCart(items, { render });
        if (!render) {
          const n = items.reduce((s, i) => s + i.quantity, 0);
          this.showStatus(`Added ${n} to your cart.`);
          await refreshCartSections();
        }
      } catch (err) {
        this.showError(err.message);
        // Docs do not promise all-or-nothing → re-read the cart and resync the drawer.
        await refreshCartSections();
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(on) {
      this.submitBtn.classList.toggle('loading', on);
      this.submitBtn.setAttribute('aria-disabled', String(on));
      this.submitBtn.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
    }

    showError(msg) {
      this.errorEl.hidden = !msg;
      this.errorEl.textContent = msg || '';
    }

    showStatus(msg) {
      this.statusEl.hidden = !msg;
      this.statusEl.textContent = msg || '';
    }
  }

  customElements.define('bloom-bundle-selector', BloomBundleSelector);

  /* ---------- <bloom-sticky-atc> ---------- */

  class BloomStickyAtc extends HTMLElement {
    connectedCallback() {
      this.selector = document.getElementById(this.dataset.target);
      if (!this.selector) return;
      this.hidden = false;
      this.metaEl = this.querySelector('[data-sticky-meta]');
      this.cta = this.querySelector('[data-sticky-cta]');

      this.chooseText = this.dataset.chooseText || 'Choose bundle';
      this.addText = this.cta.textContent.trim();
      this.preMeta = this.dataset.preMeta || '';
      this.offer = null;
      this.seenOffer = false;

      this.onOffer = (e) => {
        this.offer = e.detail;
        this.update();
      };
      this.selector.addEventListener('bloom:offer-change', this.onOffer);
      if (this.selector.form) this.selector.render?.();

      this.tiers = this.selector.querySelector('.bloom-selector__tiers');
      this.onClick = () => {
        // Before the shopper has seen the tiers, the first tap takes them there instead of blind-adding.
        if (!this.seenOffer && this.tiers) {
          this.tiers.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        const form = this.selector.querySelector('form');
        if (form?.requestSubmit) form.requestSubmit();
        else form?.dispatchEvent(new Event('submit', { cancelable: true }));
      };
      this.cta.addEventListener('click', this.onClick);

      // Visible whenever neither the tiers nor the real button are on screen (above or below).
      const button = this.selector.querySelector('[type="submit"]');
      const watched = [this.tiers, button].filter(Boolean);
      this.onScreen = new Set();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.onScreen.add(entry.target);
          else this.onScreen.delete(entry.target);
          if (entry.target === this.tiers && entry.isIntersecting) this.seenOffer = true;
        });
        this.update();
      });
      watched.forEach((el) => this.observer.observe(el));
    }

    update() {
      const show = this.onScreen ? this.onScreen.size === 0 : false;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.cta.tabIndex = show ? 0 : -1;
      const d = this.offer || {};
      if (!this.seenOffer) {
        this.cta.textContent = this.chooseText;
        this.cta.disabled = false;
        this.metaEl.textContent = this.preMeta;
      } else {
        this.cta.textContent = this.addText;
        this.cta.disabled = d.available === false;
        this.metaEl.textContent = [d.label, d.price].filter(Boolean).join(' · ');
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.selector?.removeEventListener('bloom:offer-change', this.onOffer);
      this.cta?.removeEventListener('click', this.onClick);
    }
  }

  if (!customElements.get('bloom-sticky-atc')) customElements.define('bloom-sticky-atc', BloomStickyAtc);
})();

// Product gallery: thumbnails scroll the snap track; swiping updates the active thumb;
// showMedia(mediaId) jumps to a variant's image.
if (!customElements.get('bloom-gallery')) {
  customElements.define(
    'bloom-gallery',
    class BloomGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
        if (!this.track) return;
        this.thumbs.forEach((btn) => btn.addEventListener('click', () => this.goTo(Number(btn.dataset.thumb))));
        let raf;
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => this.setActive(Math.round(this.track.scrollLeft / this.track.clientWidth)));
        });
      }
      goTo(i, smooth = true) {
        if (!this.track) return;
        this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
        this.setActive(i);
      }
      showMedia(mediaId) {
        const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
        if (i >= 0) this.goTo(i);
      }
      setActive(i) {
        this.thumbs.forEach((b, j) => (j === i ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
      }
    }
  );
}
